import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  dynamicFormTabIndexAF,
  editFormDataAF,
  EditFormMode,
  RequestBodyModel,
  RydeAdminUnit,
} from "@ryde/shared";

import { useNavigatePathsAdmin } from "./use-navigate-paths.admin";
import { useSimpleNavigation } from "./use-simple-navigation";

export const useCrudNavigateAdmin = (entity: RydeAdminUnit | "unknown") => {
  const appAdminNavigate = useNavigatePathsAdmin();
  const { navigateHome } = useSimpleNavigation();

  const [dto, setDto] = useRecoilState<RequestBodyModel>(
    editFormDataAF([entity, EditFormMode.CrudRoute]),
  );
  const [tabIndex, setTabIndex] = useRecoilState(dynamicFormTabIndexAF(entity));

  const resetEditFormPreviousData = useEvent(() => {
    /* reset edit tab index */
    if (tabIndex > 0) {
      setTabIndex(0);
    }

    /* cleanup edit dto from previous edit if not cleared */
    if (dto) {
      setDto(undefined);
    }
  });

  const navigateToList = useEvent((keepQuery = true): void => {
    if (entity === "unknown" || !appAdminNavigate[entity]) {
      return navigateHome();
    }

    return appAdminNavigate[entity].list(keepQuery);
  });

  const navigateToEdit = useEvent((id: number, keepQuery = true): void => {
    resetEditFormPreviousData();

    if (entity === "unknown" || !id || !appAdminNavigate[entity]) {
      return navigateHome();
    }

    return appAdminNavigate[entity].edit(id, keepQuery);
  });

  const navigateToPreCreateOrNew = useEvent(
    (keepQuery = true, initialData?: RequestBodyModel): void => {
      resetEditFormPreviousData();

      if (entity === "unknown" || !appAdminNavigate[entity]) {
        return navigateHome();
      }

      if (initialData) {
        setDto(initialData);
      }

      return appAdminNavigate[entity].new(keepQuery);
    },
  );

  const navigateToAddOrDuplicateAdmin = useEvent(
    (idDuplicate?: number, keepQuery = true): void => {
      if (entity === "unknown" || !appAdminNavigate[entity]) {
        return navigateHome();
      }

      return appAdminNavigate[entity].add(idDuplicate, keepQuery);
    },
  );
  return {
    navigateToList,
    navigateToEdit,
    navigateToPreCreateOrNew,
    navigateToAddOrDuplicateAdmin,
  };
};
